// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import TerceraRecoverScreenTemplate from '@front/templates/TerceraRecoverScreenTemplate.html';
import { Connection } from '@shared/commons/Connection';
import { IsPasteEvent } from '@shared/commons/KeyEventProcessor';
import { Resources } from '@shared/localizations/Resources';
import { BrowserUtils } from '@shared/utils/BrowserUtils';
import { BusinessRejectError, BusinessRejectErrorDictionary } from '@shared/utils/BusinessRejectUtils';
import { ChangePasswordStatusEnum } from '@shared/utils/Enums/ChangePasswordStatusEnum';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { TerceraMessageBox } from './TerceraMessageBox';
import { TerceraWindowBase } from './TerceraWindowBase';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
export class TerceraRecoverScreen extends TerceraWindowBase {
    constructor() {
        super(...arguments);
        this.wasPaste = false;
    }
    oninit() {
        super.oninit();
        this.observe('valueEmail', (newValue) => {
            void this.set({ valueEmailHaveError: !BrowserUtils.isEmail(newValue) });
        });
        this.observe('valueKey', (verificationCode) => {
            void this.set('valueKeyIsEmpty', !isValidString(verificationCode));
        });
        this.observe('valueNewPassword valueConfirm', () => {
            const { valueNewPassword, valueConfirm } = this.get();
            void this.set('recoverBtnEnabled', isValidString(valueNewPassword) && isValidString(valueConfirm));
        });
        this.observe('visible', (visible) => {
            if (visible) {
                this.localize();
            }
        });
        this.localize();
    }
    oncomplete() {
        super.oncomplete();
        const loginTextBox = this.Controls.loginTextBox;
        loginTextBox.on('keyDown', this.onKeyDown.bind(this));
        this.trimOnPasteAddEventListener(loginTextBox.getInputElement());
        const keyTextBox = this.Controls.keyTextBox;
        keyTextBox.on('keyDown', this.onKeyDown.bind(this));
        this.trimOnPasteAddEventListener(keyTextBox.getInputElement(), true);
    }
    onKeyDown(context) {
        if (IsPasteEvent(context.event)) {
            this.wasPaste = true;
        }
    }
    trimOnPasteAddEventListener(input, /* : HTMLInputElement */ allowOnlyDigitsInInput = false) {
        if (input == null) {
            return;
        }
        input.addEventListener('input', () => {
            if (this.wasPaste) {
                input.value = input.value.trim();
            }
            this.wasPaste = false;
            if (allowOnlyDigitsInInput) {
                input.value = input.value.replace(/\D/g, ''); /* Remove non-digit characters #126233 */
            }
        });
    }
    RecoverPassProcess(finalStage = true) {
        var _a;
        const currentConnection = (_a = MainWindowManager.LoginScreen) === null || _a === void 0 ? void 0 : _a.get('connectionParams');
        const connectServers = currentConnection === null || currentConnection === void 0 ? void 0 : currentConnection.ServerAdress.split(',').map((address) => address.trim()).filter(Boolean);
        const login = this.get('valueLogin');
        const request = {
            msgType: finalStage ? 'SendPasswordRecoveryChangeRequest' : 'SendRequestRecoveryPassword',
            dataForMsg: finalStage
                ? {
                    login,
                    newPassword: this.get('valueNewPassword'),
                    key: this.get('valueKey'),
                    accessToken: currentConnection.accessToken
                }
                : { login, email: this.get('valueEmail') }
        };
        this.SendMessagePromise(request, connectServers, currentConnection, 0).then(this.SendMessageFinish.bind(this));
    }
    SendMessageFinish(result) {
        var _a;
        if (result === null || result === 'Bad Arguments') {
            TerceraMessageBox.Show(this.get('header'), Resources.getResource('screen.RecoveryPassForm.noInternetConnection'), MessageBoxType.Warning, null, null, false, true);
            return;
        }
        // new Logic
        if (result.status === ChangePasswordStatusEnum.STATUS_ACCEPTED) {
            if (this.get('currentStep') >= TerceraRecoverScreen.NewPasswordStep) {
                this.showMSG(Resources.getResource('screen.RecoveryPassForm.Success'), true);
            }
            return;
        }
        if ((result.status === ChangePasswordStatusEnum.STATUS_DECLINED &&
            result.BusinessRejectCode === BusinessRejectError.USER_NOT_FOUND) ||
            result.status === ChangePasswordStatusEnum.STATUS_WRONG_LOGIN) {
            /* https://tp.traderevolution.com/entity/126463; https://tp.traderevolution.com/entity/130485 */
            return;
        }
        const keyByBusinessRejectCode = (_a = BusinessRejectErrorDictionary[result.BusinessRejectCode]) !== null && _a !== void 0 ? _a : 'screen.changepass.wrongKey';
        const messageMap = {
            [ChangePasswordStatusEnum.STATUS_STRONG_PAS_LOW]: { key: 'system.ChangePassLow', param: result.MinLength },
            [ChangePasswordStatusEnum.STATUS_STRONG_PAS_BASE]: {
                key: 'system.ChangePassBase',
                param: result.MinLength
            },
            [ChangePasswordStatusEnum.STATUS_STRONG_PAS_HIGH]: {
                key: 'system.ChangePassHigh',
                param: result.MinLength
            },
            [ChangePasswordStatusEnum.STATUS_LOCKED_AFTER_BRUT_FORCE]: { key: 'screen.changepass.lockedAfterBrut' },
            [ChangePasswordStatusEnum.STATUS_WRONG_OLD_OR_NEW_PASSWORD]: {
                key: 'screen.changepass.wrongOldOrNewPassword'
            },
            [ChangePasswordStatusEnum.STATUS_WRONG_KEY]: { key: 'screen.changepass.wrongKey' },
            [ChangePasswordStatusEnum.STATUS_PWD_MUST_DE_DIFFERENT]: { key: 'screen.changepass.pwdMustBeDifferent' },
            [ChangePasswordStatusEnum.STATUS_ERROR_EMAIL]: { key: 'screen.changepass.errorEmail' },
            [ChangePasswordStatusEnum.STATUS_WRONG_LOGIN]: { key: 'screen.changepass.wrongLogin' },
            [ChangePasswordStatusEnum.STATUS_WRONG_MAX_LENGTH]: { key: 'system.TooLongPass', param: result.MaxLength },
            [ChangePasswordStatusEnum.STATUS_PWD_IS_TOO_EASY]: { key: 'screen.changepass.tooeasy' },
            [ChangePasswordStatusEnum.STATUS_DECLINED]: { key: keyByBusinessRejectCode }
        };
        const messageData = messageMap[result.status];
        if (messageData) {
            this.showMSG(messageData.param !== undefined
                ? Resources.getResource(messageData.key, [messageData.param])
                : Resources.getResource(messageData.key));
            return;
        }
        // oldLogic
        this.OldLogicNotification(result.BusinessRejectCode, result.errText);
    }
    showMSG(body, withClosing = false) {
        const additionalData = {
            okText: Resources.getResource('general.messageBox.ok') // #123244
        };
        TerceraMessageBox.Show(this.get('header'), body, MessageBoxType.Warning, null, null, false, true, null, additionalData);
        if (withClosing) {
            this.close();
        }
    }
    OldLogicNotification(errCode, errText) {
        const messages = {
            381: 'screen.RecoveryPassForm.invalid_login_or_email',
            384: 'screen.RecoveryPassForm.invalid_login',
            31: 'reports.New password must differ from several previous passwords.'
        };
        const messageKey = messages[errCode];
        const message = messageKey
            ? Resources.getResource(messageKey)
            : Resources.IsResourcePresentEN(`BusinessRejectMessage.${errCode}`)
                ? Resources.getResource(`BusinessRejectMessage.${errCode}`)
                : errText;
        TerceraMessageBox.Show(this.get('header'), message, MessageBoxType.Warning, null, null, false, true);
        this.close();
    }
    SendMessagePromise(request, connectServers, currentConnection, index) {
        if (connectServers.length < index + 1) {
            return Promise.resolve('Bad Arguments');
        }
        return Connection.SendMessageToVendor(connectServers[index], currentConnection.isSSL, request.msgType, request.dataForMsg).then((responce) => {
            if (responce === null) {
                return this.SendMessagePromise(request, connectServers, currentConnection, ++index);
            }
            return responce;
        });
    }
    localize() {
        void this.set({
            textLoginLabel: Resources.getResource('screen.RecoveryPassForm.Login'),
            textEmailLabel: Resources.getResource('screen.RecoveryPassForm.Email'),
            textKeyLabel: Resources.getResource('screen.RecoveryPassForm.Key'),
            textNewPasswordLabel: Resources.getResource('screen.RecoveryPassForm.New password'),
            textConfirmLabel: Resources.getResource('screen.RecoveryPassForm.Confirm'),
            hintText: Resources.getResource('screen.RecoveryPassForm.CodeSentToEmail'),
            backText: Resources.getResource('pageNavigation.backButton'),
            nextText: Resources.getResource('pageNavigation.nextButton'),
            recoverText: Resources.getResource('screen.RecoveryPassForm.Send'),
            header: Resources.getResource('screen.RecoveryPassForm.Title'),
            progressStepLabels: [
                Resources.getResource('screen.RecoveryPassForm.Step1'),
                Resources.getResource('screen.RecoveryPassForm.Step2'),
                Resources.getResource('screen.RecoveryPassForm.Step3')
            ]
        });
    }
    clearAllAndHide() {
        void super.set({
            visible: false,
            valueEmail: '',
            valueKey: '',
            valueNewPassword: '',
            valueConfirm: '',
            newPassHided: true,
            confirmPassHided: true
        });
    }
    backClick() {
        void this.set('valueKey', ''); /* #126230 */
        const currentStep = this.get('currentStep');
        if (currentStep > TerceraRecoverScreen.CredentialStep) {
            void this.set('currentStep', currentStep - 1);
        }
    }
    nextClick() {
        const currentStep = this.get('currentStep');
        if (currentStep === TerceraRecoverScreen.CredentialStep) {
            if (!this.validateLoginAndEmail()) {
                return;
            }
            this.RecoverPassProcess(false);
        }
        else if (currentStep === TerceraRecoverScreen.NewPasswordStep) {
            if (!this.validatePasswordsMatch()) {
                return;
            }
            this.RecoverPassProcess(true);
        }
        if (currentStep < TerceraRecoverScreen.NewPasswordStep) {
            void this.set('currentStep', currentStep + 1);
        }
    }
    backBtnVisible() {
        const currentStep = this.get('currentStep');
        return currentStep > TerceraRecoverScreen.CredentialStep;
    }
    nextBtnVisible() {
        const currentStep = this.get('currentStep');
        return currentStep < TerceraRecoverScreen.NewPasswordStep;
    }
    finishBtnVisible() {
        const currentStep = this.get('currentStep');
        return currentStep === TerceraRecoverScreen.NewPasswordStep;
    }
    validateLoginAndEmail() {
        const { valueLogin, valueEmailHaveError } = this.get();
        if (!valueLogin) {
            this.showMSG(Resources.getResource('screen.changepass.emptyLogin'));
            return false;
        }
        if (valueEmailHaveError) {
            this.showMSG(Resources.getResource('screen.changepass.emptyEmail'));
            return false;
        }
        return true;
    }
    validatePasswordsMatch() {
        const { valueNewPassword, valueConfirm } = this.get();
        if (valueNewPassword !== valueConfirm) {
            this.showMSG(Resources.getResource('screen.changepass.passNotMatch'));
            return false;
        }
        return true;
    }
}
TerceraRecoverScreen.CredentialStep = 1;
TerceraRecoverScreen.VerificationCodeStep = 2;
TerceraRecoverScreen.NewPasswordStep = 3;
TerceraRecoverScreen.ClosingStep = 4;
TerceraWindowBase.extendWith(TerceraRecoverScreen, {
    partials: { bodyPartial: TerceraRecoverScreenTemplate },
    data: () => {
        return {
            width: 500,
            height: 350,
            movable: false,
            showFooter: false,
            resizable: false,
            alwaysMiddle: true,
            focused: true,
            recoverBtnEnabled: false,
            textLoginLabel: 'Login',
            textEmailLabel: 'Email',
            textKeyLabel: 'Key',
            textNewPasswordLabel: 'New Password', // NOSONAR[typescript:S2068]
            textConfirmLabel: 'Confirm',
            hintText: 'Your verification code was sent to the email specified on the previous step',
            recoverText: 'Recover',
            header: 'Password recovery',
            allowPasswordPreview: false,
            newPassHided: true,
            confirmPassHided: true,
            valueLogin: '',
            valueEmail: '',
            valueKey: '',
            valueNewPassword: '',
            valueConfirm: '',
            valueEmailHaveError: true,
            valueKeyIsEmpty: true,
            TerceraRecoverScreen,
            currentStep: TerceraRecoverScreen.CredentialStep,
            progressStepLabels: []
        };
    },
    computed: {
        nextBtnEnabled: {
            get: function () {
                const currentStep = this.get('currentStep');
                if (currentStep === TerceraRecoverScreen.CredentialStep) {
                    return isValidString(this.get('valueLogin')) && isValidString(this.get('valueEmail'));
                }
                if (currentStep === TerceraRecoverScreen.VerificationCodeStep) {
                    return !this.get('valueKeyIsEmpty');
                }
                return false;
            }
        }
    }
});
